import React, { useEffect, useState } from "react";
import "./style.css";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import { CustomerCategory, ProductAvailability } from "../../utils/constants";
import {
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import cssVariables from "../../utils/cssVariables";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning"; // For Low Stock
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn"; // For No Stock
import { SouthAfricanRand } from "../../utils/helpers";
import { round, split, trim } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../../constants";

const GridItem = ({
  title,
  price,
  invoicePrice,
  promo,
  promoInvoicePrice,
  customerCategory,
  vendor,
  image,
  productId,
  vendorImage,
  quality,
  fitment,
  availability,
  warranty,
  warrantyText,
  applicationPosition,
  applicationPositionImage,
  vendorDescription,
  showVehicleSpecificInformation,
  isKiosk,
  sku,
  quantityInCart,
}) => {
  const { handleAddToCart } = useArgusCart();
  const [updatedQuantity, setUpdatedQuantity] = useState(quantityInCart);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const fitmentImage =
    "https://argusweb.azureedge.net/argusblob01/web/fitment.jpg";
  const qualityImage =
    "https://argusweb.azureedge.net/argusblob01/web/quality.jpg";
  const availabilityImage =
    "https://argusweb.azureedge.net/argusblob01/web/availability.jpg";

  const applicationPositionParts = split(applicationPosition, ":");
  if (applicationPositionParts && applicationPositionParts.length > 1) {
    applicationPosition = trim(applicationPositionParts[1]);
  }

  let fitmentText = split(fitment, "$");
  let fitmentTextSplit = [];
  if (fitmentText) {
    if (fitmentText.length > 1) {
      fitmentTextSplit = split(fitmentText[1], "|");
    } else {
      fitmentTextSplit = fitmentText;
    }
  }

  const colourForAvailability = (() => {
    switch (availability) {
      case ProductAvailability.InStock:
        return "success";
      case ProductAvailability.LowStock:
        return "warning";
      case ProductAvailability.ThreeToFiveDays:
        return "info";
      case ProductAvailability.No_Stock:
        return "error";
      default:
        return "default";
    }
  })();

  const colourForQuality = (() => {
    switch (quality) {
      case "A+":
        return "primary";
      case "A":
        return "info";
      case "B":
        return "secondary";
      case "Other":
        return "default";
      default:
        return "default";
    }
  })();

  const handleAddToCartClick = () => {
    handleAddToCart({
      id: productId,
      name: title,
      vendor,
      image,
      sku,
    });
  };

  const handleOpenModal = (content) => {
    setModalContent(content);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalContent(null);
  };

  useEffect(() => {
    setUpdatedQuantity(Number(quantityInCart));
  }, [quantityInCart]);

  return (
    <Card
      raised
      sx={{
        display: "flex",
        flexDirection: "row",
        height: "100%",
        borderRadius: "0.125rem",
        boxShadow: "none",
        border: `0.0625rem solid ${COLORS.border}`,
      }}
    >
      {/* Left Side: Image and Vendor Logo */}
      <Box
        sx={{
          position: "relative",
          flex: "0 0 40%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: COLORS.white,
          // padding: "0.5rem",
        }}
      >
        <a href={`/product/${encodeURIComponent(sku)}`}>
          <CardMedia
            component="img"
            image={image}
            alt={title}
            sx={{
              maxHeight: "8rem",
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        </a>
        <Box
          sx={{
            position: "absolute",
            top: "0.5rem",
            left: "0.5rem",
            width: "2rem",
            height: "2rem",
          }}
        >
          {vendorImage ? (
            <Tooltip title={vendorDescription}>
              <img
                src={vendorImage}
                alt={vendor}
                style={{ height: "100%", width: "100%" }}
              />
            </Tooltip>
          ) : (
            <Box sx={{ height: "100%", width: "100%" }} />
          )}
        </Box>
      </Box>

      {/* Right Side: Product Details */}
      <Box
        sx={{
          flex: "1",
          display: "flex",
          flexDirection: "column",
          padding: "0.75rem",
          justifyContent: "space-between",
        }}
      >
        {/* Title */}
        <a
          href={`/product/${encodeURIComponent(sku)}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: cssVariables.fontSizeXS,
              fontFamily: "Rubik",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: cssVariables.lineHeightXS,
              maxHeight: "2.8em",
              minHeight: "2.8em",
              color: COLORS.darkText,
            }}
          >
            {title}
          </Typography>
        </a>

        {/* Availability, Warranty, Info Row */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ gap: "0.25rem", flexWrap: "wrap", marginTop: "0.25rem" }}
        >
          {availability && (
            <Chip
              size="small"
              label={availability}
              icon={
                availability === ProductAvailability.InStock ? (
                  <CheckCircleIcon
                    sx={{ fontSize: "1rem", color: COLORS.themeGreen }}
                  />
                ) : availability === ProductAvailability.LowStock ? (
                  <WarningIcon
                    sx={{ fontSize: "1rem", color: COLORS.themeOrange }}
                  />
                ) : availability === ProductAvailability.No_Stock ? (
                  <DoNotDisturbOnIcon
                    sx={{ fontSize: "1rem", color: COLORS.themeRed }}
                  />
                ) : null
              }
              onClick={() =>
                handleOpenModal(
                  <img
                    src={availabilityImage}
                    alt="Availability"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "37.5rem",
                      maxHeight: "25rem",
                    }}
                  />,
                )
              }
              sx={{
                fontSize: "0.625rem",
                height: "1.25rem",
                backgroundColor:
                  availability === ProductAvailability.InStock
                    ? COLORS.themeGreen
                    : availability === ProductAvailability.LowStock
                      ? COLORS.themeOrange
                      : availability === ProductAvailability.No_Stock
                        ? COLORS.themeRed
                        : COLORS.border,
                color: COLORS.white, // White text for contrast
                "& .MuiChip-icon": {
                  color: "inherit", // Ensure icon inherits the chip's color if not explicitly set
                },
              }}
            />
          )}
          {!!warranty && !!warrantyText && (
            <Tooltip title={warrantyText}>
              <Chip
                size="small"
                label={warranty}
                color="error"
                onClick={() => handleOpenModal(warrantyText)}
                sx={{ fontSize: "0.625rem", height: "1.25rem" }}
              />
            </Tooltip>
          )}
          {quality && (
            <Chip
              size="small"
              label={quality}
              color={colourForQuality || "default"}
              onClick={() =>
                handleOpenModal(
                  <img
                    src={qualityImage}
                    alt="Quality"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "37.5rem",
                      maxHeight: "25rem",
                    }}
                  />,
                )
              }
              sx={{ fontSize: "0.625rem", height: "1.25rem" }}
            />
          )}
        </Box>

        {/* SKU */}
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            fontSize: cssVariables.fontSizeXXS,
            marginTop: "0.25rem",
            color: COLORS.darkText,
            fontWeight: 300,
          }}
        >
          SKU: {sku}
        </Typography>

        {/* Price and Add to Cart Row */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginTop: "0.5rem" }}
        >
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                textDecoration: promo > 0 ? "line-through" : "none",
                fontSize: "1rem",
                fontFamily: "Rubik",
                color: COLORS.themeRed,
              }}
            >
              {SouthAfricanRand.format(round(price, 2))}
            </Typography>
            {promo > 0 && (
              <Typography
                variant="body1"
                color="error"
                sx={{ fontWeight: "bold", fontSize: "1rem" }}
              >
                {SouthAfricanRand.format(round(promo, 2))}
              </Typography>
            )}
            {invoicePrice > 0 &&
              invoicePrice !== price &&
              !promoInvoicePrice && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: "0.625rem",
                    textDecoration:
                      promoInvoicePrice > 0 ? "line-through" : "none",
                  }}
                >
                  Invoice:{" "}
                  <strong>
                    {SouthAfricanRand.format(round(invoicePrice, 2))}
                  </strong>
                </Typography>
              )}
            {promoInvoicePrice > 0 && promoInvoicePrice !== promo && (
              <Typography
                variant="body2"
                color="error"
                sx={{ fontSize: "0.625rem" }}
              >
                Invoice:{" "}
                <strong>
                  {SouthAfricanRand.format(round(promoInvoicePrice, 2))}
                </strong>
              </Typography>
            )}
            {customerCategory !== CustomerCategory.Retail && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontSize: "0.625rem" }}
              >
                Ex. VAT
              </Typography>
            )}
          </Box>
          {availability !== ProductAvailability.No_Stock && (
            <IconButton
              onClick={handleAddToCartClick}
              sx={{
                padding: "0.25rem",
                border: `0.0625rem solid ${COLORS.border}`,
                borderRadius: "2px",
              }}
            >
              <ShoppingCartIcon
                sx={{ color: COLORS.lightText, fontSize: "1.25rem" }}
              />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Modal for Additional Info */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "25rem" },
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: "0.5rem",
            boxShadow: 24,
            p: "1.5rem",
            overflowY: "auto",
          }}
        >
          <IconButton
            sx={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}
            onClick={handleCloseModal}
          >
            <CloseIcon />
          </IconButton>
          {modalContent}
        </Box>
      </Modal>
    </Card>
  );
};

export default GridItem;
